/**
 * Derivaciones del módulo de Clientes. Todo puro y testeable sin React: el
 * criterio de "qué está roto" es una decisión de producto y tiene que poder
 * auditarse sin montar una pantalla.
 *
 * Ninguna función inventa un número. Cuando el dato no está, devuelven `null`
 * para que la UI muestre el estado vacío que corresponde y no un cero.
 */
import {
  evaluateHealth,
  type Activation,
  type Client,
  type ActivationRow,
  type ClientState,
  type Health,
  type UserRef,
} from '@ops/contracts'

/** Umbrales de activación de una cuenta. Los mismos que ya usaba el prototipo. */
export const ACTIVACION_OK_PCT = 85
export const ACTIVACION_WARNING_PCT = 40

/** Un estado que lleva más de esto está trabado, no en curso. */
export const DIAS_EN_ESTADO_TRABADO = 90

/** Sin contacto por más de esto, la cuenta está a la deriva. */
export const DIAS_SIN_CONTACTO_ALERTA = 30

const MS_POR_DIA = 86_400_000

/**
 * Semáforo de la activación de una cuenta. Usa `evaluateHealth` del contrato en
 * vez de una escala propia: dos vocabularios de "está roto" terminan no
 * coincidiendo en algún lugar.
 *
 * `contratados === 0` es `unknown` y no `critical`: una cuenta sin empleados
 * contratados no tiene brecha, no tiene una brecha del 100%.
 */
export function saludActivacion(activacion: Activation | null): Health {
  if (activacion === null || activacion.contratados === 0) return 'unknown'
  return evaluateHealth({
    valor: activacion.pctActivado,
    direccion: 'higher_better',
    umbralOk: ACTIVACION_OK_PCT,
    umbralWarning: ACTIVACION_WARNING_PCT,
  })
}

/** Severidad de un estado del portfolio, para el tono de su pill. */
export function saludEstado(estado: ClientState): Health {
  switch (estado) {
    case 'pendiente_implementacion':
    case 'retencion':
      return 'critical'
    case 'implementacion':
      return 'warning'
    case 'expansion':
    case 'qbr':
      return 'ok'
    default: {
      const exhaustivo: never = estado
      return exhaustivo
    }
  }
}

/** Días enteros desde una fecha ISO. `null` cuando nunca hubo registro. */
export function diasDesde(iso: string | null, now: Date): number | null {
  if (iso === null) return null
  const entonces = new Date(iso)
  if (Number.isNaN(entonces.getTime())) return null
  return Math.max(0, Math.floor((now.getTime() - entonces.getTime()) / MS_POR_DIA))
}

/** ISO de hace N días. Para fechar un hito que solo conocemos en días. */
export function isoDesdeDias(dias: number, now: Date): string {
  return new Date(now.getTime() - dias * MS_POR_DIA).toISOString()
}

/**
 * Severidad de una fila del portfolio: lo peor de sus tres frentes.
 *
 * La activación pesa primero porque es el frente que este módulo existe para
 * mirar — una cuenta que factura con cero empleados corriendo es el caso que
 * abre la pantalla.
 */
export function saludCliente(cliente: Client, now: Date): Health {
  const activacion = saludActivacion(cliente.activacion)
  if (activacion === 'critical') return 'critical'

  const trabada =
    cliente.estado === 'pendiente_implementacion' &&
    cliente.diasEnEstado >= DIAS_EN_ESTADO_TRABADO
  if (trabada) return 'critical'

  const dias = diasDesde(cliente.ultimoContacto, now)
  const aLaDeriva = dias !== null && dias > DIAS_SIN_CONTACTO_ALERTA
  if (aLaDeriva || cliente.estado === 'retencion') return 'warning'

  return activacion
}

/**
 * Suma de MRR. `null` cuando ninguna fila tiene cifra, que es lo que pasa con
 * ops y comercial: el payload les llega con `mrr: null`, y sumar eso como cero
 * mostraría un portfolio de $0 en vez de una columna que no existe para ellos.
 */
export function sumaMrr(clientes: readonly { mrr: number | null }[]): number | null {
  const conCifra = clientes.filter((c) => c.mrr !== null)
  if (conCifra.length === 0) return null
  return conCifra.reduce((acc, c) => acc + (c.mrr ?? 0), 0)
}

export interface TotalesPortfolio {
  clientes: number
  contratados: number
  instalados: number
  brecha: number
  /** null cuando ninguna cuenta tiene medición: no es 0% activado. */
  pctActivado: number | null
  /** Cuentas sin medición de activación: la brecha real puede ser mayor. */
  sinMedicion: number
  /** Cuentas que facturan con cero empleados corriendo. */
  enCero: Client[]
  /** MRR de esas cuentas. El KPI de la pantalla. null si el rol no ve plata. */
  mrrEnCero: number | null
  mrr: number | null
}

/** Totales de un conjunto de filas. Se recalcula sobre lo que está en pantalla. */
export function totalesPortfolio(clientes: readonly Client[]): TotalesPortfolio {
  const conMedicion = clientes.filter((c) => c.activacion !== null)
  const contratados = conMedicion.reduce(
    (acc, c) => acc + (c.activacion?.contratados ?? 0),
    0,
  )
  const instalados = conMedicion.reduce(
    (acc, c) => acc + (c.activacion?.instalados ?? 0),
    0,
  )
  const enCero = clientes.filter(
    (c) => c.activacion !== null && c.activacion.contratados > 0 && c.activacion.instalados === 0,
  )

  return {
    clientes: clientes.length,
    contratados,
    instalados,
    brecha: contratados - instalados,
    pctActivado: contratados === 0 ? null : (instalados / contratados) * 100,
    sinMedicion: clientes.length - conMedicion.length,
    enCero,
    mrrEnCero: sumaMrr(enCero),
    mrr: sumaMrr(clientes),
  }
}

/** Peor primero. `unknown` antes que `ok`: una cuenta sin medición es un agujero. */
const RANGO_SALUD: Record<Health, number> = {
  critical: 0,
  warning: 1,
  unknown: 2,
  ok: 3,
}

/**
 * Orden por riesgo. Es el orden **base** de la tabla, el que el tercer click en
 * un encabezado devuelve: en una herramienta de ops el orden útil por defecto es
 * "lo más roto arriba", no el alfabético.
 */
export function ordenarPorRiesgo(clientes: readonly Client[], now: Date): Client[] {
  return [...clientes].sort((a, b) => {
    const porSalud = RANGO_SALUD[saludCliente(a, now)] - RANGO_SALUD[saludCliente(b, now)]
    if (porSalud !== 0) return porSalud
    const brechaA = a.activacion?.brecha ?? 0
    const brechaB = b.activacion?.brecha ?? 0
    if (brechaA !== brechaB) return brechaB - brechaA
    if ((a.mrr ?? 0) !== (b.mrr ?? 0)) return (b.mrr ?? 0) - (a.mrr ?? 0)
    return a.nombre.localeCompare(b.nombre, 'es')
  })
}

/** Verticales presentes, ordenadas en español. Para el filtro. */
export function verticalesDe(clientes: readonly Client[]): string[] {
  return [...new Set(clientes.map((c) => c.vertical))].sort((a, b) =>
    a.localeCompare(b, 'es'),
  )
}

/** Dueños presentes, sin repetir. Para el filtro. */
export function duenosDe(clientes: readonly Client[]): UserRef[] {
  const porId = new Map<string, UserRef>()
  for (const cliente of clientes) {
    if (cliente.owner !== null) porId.set(cliente.owner.id, cliente.owner)
  }
  return [...porId.values()].sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'))
}

export interface FiltrosPortfolio {
  estado: string
  vertical: string
  ownerId: string
  q: string
}

export const FILTROS_VACIOS: FiltrosPortfolio = {
  estado: '',
  vertical: '',
  ownerId: '',
  q: '',
}

export function hayFiltros(filtros: FiltrosPortfolio): boolean {
  return (
    filtros.estado !== '' ||
    filtros.vertical !== '' ||
    filtros.ownerId !== '' ||
    filtros.q.trim() !== ''
  )
}

/** Sin acentos y en minúsculas: buscar "logistica" tiene que encontrar "Logística". */
function normalizar(texto: string): string {
  return texto
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
}

/**
 * Filtra en el cliente y no en la API a propósito: el portfolio son decenas de
 * filas, el contador "N de M" necesita las dos cifras a la vez, y el filtro por
 * vertical no existe como parámetro del endpoint.
 */
export function filtrarClientes(
  clientes: readonly Client[],
  filtros: FiltrosPortfolio,
): Client[] {
  const q = normalizar(filtros.q.trim())
  return clientes.filter((cliente) => {
    if (filtros.estado !== '' && cliente.estado !== filtros.estado) return false
    if (filtros.vertical !== '' && cliente.vertical !== filtros.vertical) return false
    if (filtros.ownerId !== '' && (cliente.owner?.id ?? '') !== filtros.ownerId) {
      return false
    }
    if (q !== '') {
      const heno = normalizar(`${cliente.nombre} ${cliente.vertical}`)
      if (!heno.includes(q)) return false
    }
    return true
  })
}

/**
 * Fila del ranking de brecha: la del endpoint de activación, enriquecida con lo
 * que solo está en el portfolio (MRR, estado, antigüedad). El join es por id y
 * lo que no aparece queda en `null`, nunca en cero.
 */
export interface FilaBrecha extends ActivationRow {
  vertical: string | null
  estado: ClientState | null
  diasEnEstado: number | null
  mrr: number | null
  ultimoContacto: string | null
}

export function filasBrecha(
  filas: readonly ActivationRow[],
  clientes: readonly Client[],
): FilaBrecha[] {
  const porId = new Map(clientes.map((c) => [c.id, c]))
  return filas
    .filter((fila) => fila.activacion.brecha > 0)
    .map((fila) => {
      const cliente = porId.get(fila.clientId)
      return {
        ...fila,
        vertical: cliente?.vertical ?? null,
        estado: cliente?.estado ?? null,
        diasEnEstado: cliente?.diasEnEstado ?? null,
        mrr: cliente?.mrr ?? null,
        ultimoContacto: cliente?.ultimoContacto ?? null,
      }
    })
}

/**
 * El peor caso: mayor brecha primero y, empatados, el que lleva más días
 * trabado. Es el que la pantalla tiene que dejar legible como tal.
 */
export function peorCaso(filas: readonly FilaBrecha[]): FilaBrecha | null {
  if (filas.length === 0) return null
  return [...filas].sort((a, b) => {
    const porBrecha = b.activacion.brecha - a.activacion.brecha
    if (porBrecha !== 0) return porBrecha
    return (b.diasEnEstado ?? 0) - (a.diasEnEstado ?? 0)
  })[0] as FilaBrecha
}
